import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useAuth } from '@/lib/auth-context';
import { useFeatureFlags } from '@/hooks/use-feature-flags';

// ─── Typed Interfaces ────────────────────────────────────────────────────────

export interface Area {
  id: string;
  name: string;
  siteId: string;
}

export interface Site {
  id: string;
  name: string;
  regionId?: string;
  areas: Area[];
}

export interface Region {
  id: string;
  name: string;
}

export interface Project {
  id: string;
  name: string;
}

export interface ScopeContextType {
  selectedWorksite: Site | null;
  setSelectedWorksite: (site: Site | null) => void;
  selectedArea: Area | null;
  setSelectedArea: (area: Area | null) => void;
  worksites: Site[];
  regions: Region[];
  selectedRegion: Region | null;
  tenantName: string | null;
  isLoading: boolean;
  // Project / multiSite
  projects: Project[];
  selectedProject: Project | null;
  setSelectedProject: (p: Project | null) => void;
  multiSiteEnabled: boolean;
  // Assignment indicator — true when the current site was pre-selected
  // because the logged-in user has linked worker site assignments.
  isAssignmentFiltered: boolean;
  // Error / retry
  error: string | null;
  reload: () => void;
}

const ScopeContext = createContext<ScopeContextType | undefined>(undefined);

async function safeFetch(url: string): Promise<{ status: number; data: any }> {
  const res = await fetch(url, { credentials: 'include' });
  if (!res.ok) return { status: res.status, data: null };
  try {
    const data = await res.json();
    return { status: res.status, data };
  } catch {
    return { status: res.status, data: null };
  }
}

export const ScopeProvider = ({ children }: { children: ReactNode }) => {
  const [worksites, setWorksites] = useState<Site[]>([]);
  const [regions, setRegions] = useState<Region[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedWorksite, setSelectedWorksiteState] = useState<Site | null>(null);
  const [selectedArea, setSelectedArea] = useState<Area | null>(null);
  const [selectedProject, setSelectedProjectState] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAssignmentFiltered, setIsAssignmentFiltered] = useState(false);

  const {
    isAuthenticated,
    isLoading: authIsLoading,
    logout,
    activeTenant,
    currentUser,
  } = useAuth();

  const { isEnabled } = useFeatureFlags();
  const multiSiteEnabled = isEnabled('platform.multiSite');

  const tenantName = activeTenant?.name ?? null;
  const tenantId = activeTenant?.id;

  const selectedRegion = selectedWorksite?.regionId
    ? regions.find(r => r.id === selectedWorksite.regionId) ?? null
    : null;

  // Stable localStorage key helpers — depend on tenantId only.
  const scopeSiteKey = tenantId ? `nashid.scope.v1.${tenantId}.siteId` : null;
  const scopeProjectKey = tenantId ? `nashid.scope.v1.${tenantId}.projectId` : null;

  const setSelectedWorksite = useCallback((site: Site | null) => {
    setSelectedWorksiteState(site);
    setSelectedArea(null);
    if (scopeSiteKey && site) {
      try { localStorage.setItem(scopeSiteKey, site.id); } catch {}
    }
  }, [scopeSiteKey]);

  const setSelectedProject = useCallback((p: Project | null) => {
    setSelectedProjectState(p);
    // Switching project resets Site and Area to "All"
    setSelectedWorksiteState(null);
    setSelectedArea(null);
    if (scopeProjectKey) {
      try {
        if (p) {
          localStorage.setItem(scopeProjectKey, p.id);
        } else {
          localStorage.removeItem(scopeProjectKey);
        }
      } catch {}
    }
  }, [scopeProjectKey]);

  const handleSessionExpired = useCallback(async () => {
    const currentPath = window.location.pathname;
    if (currentPath.startsWith('/auth/')) return;
    await logout();
    window.location.href = '/auth/login?reason=session_expired';
  }, [logout]);

  const loadScope = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const fetches: Promise<{ status: number; data: any }>[] = [
        safeFetch('/api/sites'),
        safeFetch('/api/areas'),
        safeFetch('/api/regions'),
      ];
      if (multiSiteEnabled) {
        fetches.push(safeFetch('/api/projects'));
      }

      const results = await Promise.all(fetches);
      const [sitesResult, areasResult, regionsResult, projectsResult] = results;

      if (
        sitesResult.status === 401 ||
        areasResult.status === 401 ||
        regionsResult.status === 401 ||
        (projectsResult && projectsResult.status === 401)
      ) {
        await handleSessionExpired();
        return;
      }

      const sitesList: any[] = sitesResult.data || [];
      const areasList: any[] = areasResult.data || [];
      const regionsList: Region[] = regionsResult.data || [];
      const projectsList: Project[] = projectsResult?.data || [];

      setRegions(regionsList);

      if (multiSiteEnabled) {
        setProjects(projectsList);
      }

      const enrichedSites: Site[] = sitesList.map(site => ({
        ...site,
        areas: areasList.filter((a: any) => a.siteId === site.id),
      }));
      setWorksites(enrichedSites);

      // ─── Default site selection priority order ────────────────────────
      // 1. User's linked worker site IDs (first assigned site)
      // 2. localStorage persisted site ID
      // 3. URL ?siteId= param
      // 4. First site in list (current behavior)
      let defaultSite: Site | null = null;
      let assignmentFiltered = false;

      // Priority 1: linked worker site IDs
      const linkedSiteIds = currentUser?.linkedWorkerSiteIds ?? [];
      if (linkedSiteIds.length > 0) {
        for (const sid of linkedSiteIds) {
          const found = enrichedSites.find(s => s.id === sid);
          if (found) {
            defaultSite = found;
            assignmentFiltered = true;
            break;
          }
        }
      }

      // Priority 2: localStorage
      if (!defaultSite && scopeSiteKey) {
        try {
          const stored = localStorage.getItem(scopeSiteKey);
          if (stored) {
            const found = enrichedSites.find(s => s.id === stored);
            if (found) defaultSite = found;
          }
        } catch {}
      }

      // Priority 3: URL param ?siteId=
      if (!defaultSite) {
        try {
          const urlSiteId = new URLSearchParams(window.location.search).get('siteId');
          if (urlSiteId) {
            const found = enrichedSites.find(s => s.id === urlSiteId);
            if (found) defaultSite = found;
          }
        } catch {}
      }

      // Priority 4: fallback to first site
      if (!defaultSite && enrichedSites.length > 0) {
        defaultSite = enrichedSites[0];
      }

      setSelectedWorksiteState(defaultSite);
      setIsAssignmentFiltered(assignmentFiltered);

      // ─── Default project selection (multiSite only) ────────────────────
      if (multiSiteEnabled && projectsList.length > 0) {
        let defaultProject: Project | null = null;

        if (scopeProjectKey) {
          try {
            const stored = localStorage.getItem(scopeProjectKey);
            if (stored) {
              const found = projectsList.find(p => p.id === stored);
              if (found) defaultProject = found;
            }
          } catch {}
        }

        if (!defaultProject) {
          try {
            const urlProjectId = new URLSearchParams(window.location.search).get('projectId');
            if (urlProjectId) {
              const found = projectsList.find(p => p.id === urlProjectId);
              if (found) defaultProject = found;
            }
          } catch {}
        }

        setSelectedProjectState(defaultProject);
      }
    } catch (err) {
      console.error('Failed to load scope', err);
      setError('Failed to load scope data');
    } finally {
      setIsLoading(false);
    }
  }, [
    isAuthenticated,
    multiSiteEnabled,
    scopeSiteKey,
    scopeProjectKey,
    handleSessionExpired,
    currentUser?.linkedWorkerSiteIds,
  ]);

  useEffect(() => {
    if (authIsLoading) return;
    if (!isAuthenticated) {
      setIsLoading(false);
      return;
    }
    loadScope();
  }, [isAuthenticated, authIsLoading, loadScope]);

  const reload = useCallback(() => {
    loadScope();
  }, [loadScope]);

  return (
    <ScopeContext.Provider value={{
      worksites,
      regions,
      selectedRegion,
      selectedWorksite,
      setSelectedWorksite,
      selectedArea,
      setSelectedArea,
      tenantName,
      isLoading,
      projects,
      selectedProject,
      setSelectedProject,
      multiSiteEnabled,
      isAssignmentFiltered,
      error,
      reload,
    }}>
      {children}
    </ScopeContext.Provider>
  );
};

export const useScope = () => {
  const context = useContext(ScopeContext);
  if (!context) {
    throw new Error('useScope must be used within a ScopeProvider');
  }
  return context;
};
